import type {
  KeyVal,
  KeyValPart,
  ProductInformation,
} from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import get from 'es-toolkit/compat/get';
import keys from 'es-toolkit/compat/keys';
import { cicAttributeMap } from '../cicAttributeMap';
import { decodeItemCode, toTitleCase } from '../utilities';
import {
  cicMerchandisingTags,
  cicProductTypeName,
  cicProductTypeSlug,
} from './catalogMapping';
import type { NormalizedCiCGroup, NormalizedCiCItem } from './types';

export interface SpecBuilderOptions {
  config?: {
    filterConfig: Record<
      string,
      | {
          displayName?: string;
          uom?: string;
          /**
           * `specs` translation keys overriding vendor value labels, keyed by
           * value code. Categorical attributes only: ignored, with a warning,
           * when `uom` is also set.
           */
          valueDisplayNames?: Record<string, string>;
        }
      | undefined
    >;
  };
  localizations?: Record<string, string>;
}

function buildSpecFromAttribute(
  key: string,
  item: NormalizedCiCItem,
  options?: SpecBuilderOptions
): KeyVal | null {
  const propertyPath = cicAttributeMap[key];
  const spec = get(item, propertyPath);
  if (spec === null || spec === undefined) return null;

  const filterConfig = options?.config?.filterConfig[key.toLowerCase()];
  const convertedValues: string[] = [];

  if (Array.isArray(spec)) {
    convertedValues.push(...spec);
  } else if (typeof spec === 'boolean') {
    convertedValues.push(spec ? 'Yes' : 'No');
  } else {
    convertedValues.push(spec.toString());
  }

  const uom = filterConfig?.uom ?? '';
  let valueDisplayNames = filterConfig?.valueDisplayNames;
  if (uom && valueDisplayNames) {
    logger.warn(
      `Ignoring valueDisplayNames for "${key}": value overrides are not supported on attributes with a unit of measure ("${uom}").`
    );
    valueDisplayNames = undefined;
  }

  const valueParts: KeyValPart[] = [];
  for (const value of convertedValues) {
    let label = value;
    if (
      options?.localizations &&
      propertyPath.indexOf('characteristics.') !== -1
    ) {
      const parts = propertyPath.split('.');
      const localeKey = `${parts.pop()}.${value}`;
      label = options.localizations[localeKey] ?? value;
    }
    if (valueParts.some((part) => part.label === label)) continue;
    valueParts.push({ label, messageKey: valueDisplayNames?.[value] });
  }

  return {
    key: filterConfig?.displayName || toTitleCase(key),
    value: `${valueParts.map((part) => part.label).join(', ')} ${uom}`.trim(),
    ...(valueParts.some((part) => part.messageKey) ? { valueParts } : {}),
  };
}

/**
 * Builds product specs array from a normalized CiC item.
 *
 * Pass optional `config` and `localizations` (available server-side) to get
 * localized display names and unit-of-measure suffixes. Without them the keys
 * fall back to title-cased attribute names and no UOM is appended.
 */
export function buildProductSpecs(
  item: NormalizedCiCItem,
  brandName: string,
  productName: string,
  sku: string,
  options?: SpecBuilderOptions
): KeyVal[] {
  const specs = Object.keys(cicAttributeMap)
    .sort()
    .map((key) => buildSpecFromAttribute(key, item, options))
    .filter((spec): spec is KeyVal => spec !== null);

  specs.unshift(
    { key: 'Brand', value: brandName },
    { key: 'Product Line', value: productName },
    { key: 'Color', value: item.names?.main || '' },
    { key: 'SKU #', value: sku }
  );

  if (item.dimensions?.width) {
    specs.push({
      key: 'Width',
      value: `${item.dimensions.width.toString()} Inches`,
    });
  }

  // CiC has no `length` dimension: plank/tile length is carried on `depth`.
  if (item.dimensions?.depth) {
    specs.push({
      key: 'Length',
      value: `${item.dimensions.depth.toString()} Inches`,
    });
  }

  if (item.dimensions?.height) {
    const height = Number(item.dimensions.height);
    specs.push({
      key: 'Thickness',
      value: `${height.toFixed(2)} Inches`,
    });
  }

  return specs;
}

/**
 * Depth-first list of a group tree: every group plus all of its descendants.
 * Catalogs nest to arbitrary depth (brand > collection > product line), and
 * `item.groupRefs` may point at a group on any level.
 */
export function flattenItemGroups(
  groups: NormalizedCiCGroup[]
): NormalizedCiCGroup[] {
  const flat: NormalizedCiCGroup[] = [];
  const visit = (group: NormalizedCiCGroup) => {
    flat.push(group);
    for (const child of group.groups ?? []) visit(child);
  };
  for (const group of groups) visit(group);
  return flat;
}

/**
 * Finds the brand and product-line (falls back to collection) groups for a
 * normalized item by matching item.groupRefs against NormalizedCiCGroup.id.
 */
export function extractItemGroups(
  item: NormalizedCiCItem,
  groups: NormalizedCiCGroup[]
): {
  brandGroup: NormalizedCiCGroup | undefined;
  productGroup: NormalizedCiCGroup | undefined;
} {
  const allGroups = flattenItemGroups(groups);
  const itemGroups = item.groupRefs?.map((ref) =>
    allGroups.find((g) => g.id === ref)
  );
  return {
    brandGroup: itemGroups?.find((g) => g?.type === 'itemGroup.brand'),
    productGroup:
      itemGroups?.find((g) => g?.type === 'itemGroup.productLine') ??
      itemGroups?.find((g) => g?.type === 'itemGroup.collection'),
  };
}

/**
 * CIC marks variants that must render as standalone SRP cards with
 * `refCodes.others.spread = "true"`.
 */
export function isSpreadItem(item: {
  refCodes?: { others?: Record<string, string> };
}): boolean {
  return item.refCodes?.others?.['spread'] === 'true';
}

export interface BuildProductInformationParams {
  brandName: string;
  productName: string;
  swatches: ProductInformation['swatches'];
  /** Room scene image URLs. Backend-only; omit on the frontend. */
  roomscenes?: string[];
  /** Country of origin. Backend-only; omit on the frontend. */
  countryOfOrigin?: string;
  features?: ProductInformation['features'];
}

/**
 * Assembles a `ProductInformation` object from a normalized CiC item and
 * caller-resolved context (slug, brand/product names, product type, swatches).
 *
 * Handles the fields that are identical across backend and frontend:
 * - description cleanup
 * - UPID, sku, unitPrice
 * - tags + hasVisualization/hasSample from context.merchandising
 * - files from moreInfoLinks
 * - specs via buildProductSpecs (pass config + localizations for server-side enrichment)
 * - priceDescription constant
 */
export function buildProductInformation(
  item: NormalizedCiCItem,
  params: BuildProductInformationParams,
  options?: SpecBuilderOptions
): ProductInformation {
  const sku = item.refCodes?.sku ?? '';
  const { slug } = decodeItemCode(item.code);
  const colorName = item.names?.main || '';
  const { tags, hasSample, hasVisualization } = cicMerchandisingTags(
    item.context?.merchandising
  );
  const baseItemType = item.classification?.baseItemType ?? '';

  // Clean up characteristics
  const characterstics = item.classification?.characteristics ?? {};
  delete characterstics['targetRooms'];

  return {
    productId: item.id,
    UPID: item.refCodes?.manufCode || '',
    sku,
    slug,
    colorName,
    brandName: params.brandName,
    productName: params.productName,
    productType: cicProductTypeName(baseItemType),
    productTypeSlug: cicProductTypeSlug(baseItemType),
    description:
      item.descriptions?.main?.replace(/No description available/gi, '') || '',
    tags,
    hasVisualization,
    hasSample,
    specs: buildProductSpecs(
      item,
      params.brandName,
      params.productName,
      sku,
      options
    ),
    swatches: params.swatches,
    roomscenes: keys(item.images?.others).map(
      (k) => item.images?.others?.[k].uri ?? ''
    ),
    countryOfOrigin: item.classification?.others?.['countryOfOrigin'] ?? '',
    files:
      item.moreInfoLinks?.map((link) => ({
        type: link.type,
        uri: link.uri,
      })) ?? [],
    unitPrice: Number(item.refCodes?.others?.['pricing'] ?? 0),
    priceDescription: 'Sq Ft',
    features: params.features,
  };
}
